import { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout from '@/components/Layout'
import { Badge, Button, Chip, Field, Fold, List, Row, Section, fieldInputClass } from '@/components/ui'
import { Sheet, SheetPad } from '@/components/Sheet'
import { toast } from '@/components/Toast'
import { t } from '@/lib/i18n'

// 「📷 拍黑板」的入口在主页面（Index.tsx 的空态与照片条），不在这一页 ——
// 这一页是「把文字粘进来分条」，照片走的是另一条路径（S6 Task 4）。

interface PreviewItem {
  body: string
  kind: 'todo' | 'note'
  needs_signature: boolean
  due_on: string | null
  points: number
}
interface PreviewGroup { subject: string; items: PreviewItem[] }

interface Props {
  text: string
  viewed_date: string
  recorded: boolean
  settings: { points_per: number; daily_cap: number | null; standing: string }
  /** 那排「每条默认」chip 的按下态。服务端回显 —— 不回显的话重新解析一次就跳回设置值了。 */
  default_points: number
  preview: PreviewGroup[]
}

const POINTS = [0, 1, 2, 3, 5, 10]

// 稿子 `.qbar` 里那几个键的形状。44px 高，不是小字链接。
const qkey = 'min-h-tap px-3.5 rounded-ctl font-bold text-[13px]'

// 预览里的一条，加上「被去掉了没有」这个只在本地存在的状态。
type Draft = PreviewItem & { subject: string; key: string; removed: boolean }

export default function Compose({ text: initialText, viewed_date, recorded, settings, preview, default_points }: Props) {
  const [text, setText] = useState(initialText)
  // 解析结果放本地 state：手动翻过的（打卡项 ⇄ 要求、改分值、✕ 去掉）都改这里。
  const [drafts, setDrafts] = useState<Draft[]>(() => flatten(preview))
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [defaultPoints, setDefaultPoints] = useState(String(default_points))

  // ⚠️ Inertia 的 POST 回到**同一个页面组件**时 React 不会重挂，于是 useState 的
  // 初始值不再求值 —— 重新解析之后 drafts 会一直停在上一次那份（第一次进来是空的，
  // 表现就是「点了重新解析但预览不出来」）。实测踩过。
  //
  // 所以自己盯着 props 重播一次。用服务端回显的原文当身份：它变了就说明这是
  // 新一次解析。这是 React 官方的「props 变化时调整 state」写法（在渲染里 setState，
  // 不是 useEffect —— 后者会先渲染一帧旧数据）。
  const [seededText, setSeededText] = useState(initialText)
  if (seededText !== initialText) {
    setSeededText(initialText)
    setDrafts(flatten(preview))
    setText(initialText)
  }

  const kept = drafts.filter((d) => !d.removed)
  const removed = drafts.filter((d) => d.removed)
  const todoCount = kept.filter((d) => d.kind === 'todo').length
  const noteCount = kept.filter((d) => d.kind === 'note').length

  const patch = (key: string, change: Partial<Draft>) =>
    setDrafts((all) => all.map((d) => (d.key === key ? { ...d, ...change } : d)))

  // 改上面那段原文会**重新分一遍**，覆盖下面手动翻过的。这是刻意的：
  // 原文是唯一的文字出处，两处都能改文字必然打架（设计稿 §三）。
  const reparse = (points = defaultPoints) =>
    router.post('/homework/preview', { text, date: viewed_date, points })

  // 「用示例试试」：稿子里这是空态引导的后半句（「点粘贴，再点用示例试试」）。
  // 用户给的那段真作业，一字未改 —— 它同时是 HomeworkParser 测试里钉住的样本。
  const SAMPLE = [
    '## 英语',
    '1. 默写纸（需签名，重默）',
    '2. 课本 P5-P6，朗读 5 遍',
    '## 语文',
    '1. B 本完成第 2 课，黑色水笔书写，保持卷面干净整洁，禁止涂改',
    '2. 小练笔：选取一个场景介绍自己的学校（书本 P4 习题）',
    '3. 阅读：下发《窗边的小豆豆》，下周四回收书本 + 摘抄本',
    '## 数学',
    '1. 今日学习：课本 P3',
    '2. 回家作业注意书写格式：',
    '3. 等号写在算式左下方；做题口诀：1 划、2 算、3 落下；划线要用尺子。',
  ].join('\n')

  // 读剪贴板要用户手势 + 权限。被拒就**如实说**并告诉他怎么手动做，
  // 不装作粘上了 —— 那会让人以为解析器坏了。
  const pasteFromClipboard = async () => {
    try {
      const clip = await navigator.clipboard.readText()
      if (!clip.trim()) {
        toast(t('homework.clipboard_empty'))
        return
      }
      setText(clip)
      toast(t('homework.clipboard_ok'))
    } catch {
      toast(t('homework.clipboard_denied'))
    }
  }

  // 常驻作业（每天都有的那几条）一点插进原文框，再和老师那段一起解析（R13）。
  const insertStanding = () => {
    setText((current) => (current ? `${current}\n${settings.standing}` : settings.standing))
    toast(t('homework.standing_inserted'))
  }

  const save = (mode: 'append' | 'replace') => {
    setConfirmReplace(false)
    router.post('/homework', {
      date: viewed_date,
      source: 'paste',
      mode,
      items: kept.map((d) => ({
        subject: d.subject, body: d.body, kind: d.kind,
        points: d.points, needs_signature: d.needs_signature, due_on: d.due_on ?? '',
      })),
    }, {
      onSuccess: () => toast(mode === 'append' ? t('homework.toast_appended') : t('homework.toast_saved')),
    })
  }

  // 分值一点循环改：判错的成本必须接近零，所以不弹面板、就地循环（设计稿 §三）。
  const cyclePoints = (d: Draft) => {
    const next = POINTS[(POINTS.indexOf(d.points) + 1) % POINTS.length]
    patch(d.key, { points: next })
  }

  return (
    <Layout bleed topbar={{
      back: `/homework?date=${viewed_date}`,
      title: t('homework.compose_title'),
      // 稿子的 `#inCnt`：这一份要保存几件，随手动翻转 / 去掉实时变
      right: <span className="text-faint text-[13px]">{t('homework.item_count', { count: todoCount })}</span>,
    }}>
      <Section title={t('homework.compose_label')}>
        <div className="bg-card rounded-card shadow-card p-3.5">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={7}
            placeholder={t('homework.compose_placeholder')}
            className={fieldInputClass}
          />
          {/* 稿子的 `.qbar` 五个键。第五个（📷 拍黑板）的入口在主页面，不在这里。 */}
          <div className="flex gap-2 flex-wrap mt-2.5">
            <button type="button" onClick={pasteFromClipboard} className={qkey + ' bg-tint text-brand'}>
              📋 {t('homework.paste_clipboard')}
            </button>
            <button type="button" onClick={() => setText(SAMPLE)} className={qkey + ' bg-sunk text-muted'}>
              {t('homework.try_sample')}
            </button>
            {settings.standing.trim() && (
              <button type="button" onClick={insertStanding} className={qkey + ' bg-sunk text-muted'}>
                ＋ {t('homework.standing_cta', { count: settings.standing.trim().split('\n').length })}
              </button>
            )}
            <button
              type="button"
              disabled={!text.trim()}
              onClick={() => setText('')}
              className={qkey + ' bg-sunk text-muted disabled:opacity-40 disabled:pointer-events-none'}
            >
              {t('homework.clear')}
            </button>
          </div>

          {/* 稿子的「每条默认」：改了**立刻重算整份预览**，不用退出去改设置再回来
              重新粘一遍。手动改过分值的那几条会被覆盖 —— 与「改原文重新分一遍」同理。 */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="text-[13px] font-bold text-muted">{t('homework.default_points')}</span>
            {[ 1, 2, 3, 5 ].map((p) => (
              <Chip
                key={p}
                pressed={defaultPoints === String(p)}
                onClick={() => {
                  setDefaultPoints(String(p))
                  if (text.trim()) reparse(String(p))
                }}
              >
                {p}
              </Chip>
            ))}
          </div>

          <div className="mt-3">
            <Button variant="ghost" disabled={!text.trim()} onClick={() => reparse()}>
              {t('homework.reparse')}
            </Button>
          </div>
          <Fold summary={t('homework.how_it_parses')}>
            <p className="m-0 px-1 text-muted text-[13px] leading-[1.6] whitespace-pre-line">
              {t('homework.parse_rules')}
            </p>
          </Fold>
        </div>
      </Section>

      {kept.length > 0 && (
        <Section
          title={t('homework.preview_title')}
          tail={t('homework.preview_count', { todo: todoCount, note: noteCount })}
        >
          <List>
            {kept.map((d) => (
              <Row
                key={d.key}
                lead={d.kind === 'note' ? <span aria-hidden="true" className="text-amber">⚠️</span> : '✓'}
                title={d.body}
                sub={
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <span>{d.subject}</span>
                    {d.due_on && <Badge tone="brand">🗓 {d.due_on.slice(5)}</Badge>}
                    {d.needs_signature && <Badge tone="amber">{t('homework.needs_sign')}</Badge>}
                  </span>
                }
                // 分值胶囊一点循环改；「要求」不给分值（它不可完成）
                pts={d.kind === 'note' ? undefined : (
                  <button type="button" onClick={() => cyclePoints(d)} aria-label={t('homework.cycle_points_aria')}>
                    +{d.points}
                  </button>
                )}
                // 整行点一下在「打卡项 ⇄ 要求」之间翻 —— 判错的成本接近零
                onTap={() => patch(d.key, { kind: d.kind === 'todo' ? 'note' : 'todo' })}
                ariaLabel={d.kind === 'todo' ? t('homework.flip_to_note') : t('homework.flip_to_todo')}
                right={
                  <button
                    type="button"
                    onClick={() => patch(d.key, { removed: true })}
                    aria-label={t('homework.remove_aria', { body: d.body })}
                    className="shrink-0 min-w-tap min-h-tap rounded-chip text-muted text-base active:bg-sunk active:text-red"
                  >
                    ✕
                  </button>
                }
              />
            ))}
          </List>
        </Section>
      )}

      {removed.length > 0 && (
        <Fold summary={t('homework.removed_fold', { count: removed.length })}>
          <List>
            {removed.map((d) => (
              <Row
                key={d.key}
                done
                title={d.body}
                sub={d.subject}
                right={
                  <button
                    type="button"
                    onClick={() => patch(d.key, { removed: false })}
                    className="shrink-0 min-h-tap px-3 rounded-chip bg-tint text-brand font-bold text-xs"
                  >
                    {t('homework.restore')}
                  </button>
                }
              />
            ))}
          </List>
        </Fold>
      )}

      {kept.length > 0 && (
        <>
          <div className="h-[74px]" />
          <div className="fixed left-3 right-3 z-[35] above-tabbar max-w-[536px] mx-auto grid gap-2">
            {/* 当天**已经有**作业时主按钮是「追加」，「替换全部」退成次要 ——
                语文老师和数学老师分两次发是常态，默认替换会把先录的那半抹掉，
                而那半可能已经打过卡了（设计稿 R7）。 */}
            <Button onClick={() => save(recorded ? 'append' : 'replace')}>
              {recorded
                ? t('homework.append_n', { count: todoCount })
                : t('homework.save_n', { count: todoCount })}
            </Button>
            {recorded && (
              <Button variant="plain" onClick={() => setConfirmReplace(true)}>{t('homework.replace')}</Button>
            )}
          </div>
        </>
      )}

      {/* 「替换全部」会删掉已录的条目（含已打卡的），属破坏性操作 —— 过一层确认。 */}
      <Sheet
        open={confirmReplace}
        onClose={() => setConfirmReplace(false)}
        label={t('homework.replace')}
        title={t('homework.replace_title')}
      >
        <div className="mx-5 mb-3.5 px-3.5 py-3 rounded-ctl bg-red-tint text-red-strong text-[13px] leading-[1.45]">
          {t('homework.replace_warn')}
        </div>
        <SheetPad>
          <div className="grid gap-2">
            <Button variant="danger" onClick={() => save('replace')}>{t('homework.replace_confirm')}</Button>
            <Button variant="plain" onClick={() => setConfirmReplace(false)}>{t('people.keep')}</Button>
          </div>
        </SheetPad>
      </Sheet>
    </Layout>
  )
}

// 服务端给的是「按学科分组」，本地拍平成一条一条 —— 翻转 / 改分值 / 去掉都是逐条的。
// subject 跟着条目走，保存时再由服务端按 subject 重新编号。
function flatten(groups: PreviewGroup[]): Draft[] {
  return groups.flatMap((g, gi) =>
    g.items.map((item, ii) => ({ ...item, subject: g.subject, key: `${gi}-${ii}`, removed: false })),
  )
}
